
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter, Action } from "vuex-class";
import { FilteredEvent } from "@/store/eventqueue/types";

import {
	getEnhancedMapping,
	IEnhancedMapping,
	IMap,
	IMarker,
	ILayer,
	ILatLng,
	ILatLngBounds,
	convertStringToLatLng,
	addMeterToCoord,
	stringToBounds,
	union,
	extend
} from "@sureview/v2-mapping";

import { darkMapStyle, satelliteMapStyle, getMapStyle } from "@/scripts/mapping/mapStyle";
import { axiosInstance } from "@/axios.instance";
import { relativeTimeRounding } from "moment";
import Multiselect from "vue-multiselect";
import { retry } from "rxjs/operators";
import { AlarmQueueFilterID } from "@/store/eventqueue/types";
import { get } from "lodash";
import { FeaturesList, UserGroup } from "@/store/types";
import { MapRequest, MapLayer } from "@/store/map-layers/types";
import { AxiosResponse } from "axios";
import api from "@/services/api.service";
import { GPSService, GeoCodeLocation } from "@/services/gps.service";

class EventRecordPositionMap {
	[s: number]: IMarker;
}

class EventIconsMap {
	[s: number]: Array<IMarker>;
}

class MapLayerData {
	mapLayerID: number = 0;
	minElevation: number | null = null;
	maxElevation: number | null = null;

	title: string = "";
	ne: string = "";
	sw: string = "";

	rotation: number = 0;
}

class LayerMap {
	[s: number]: ILayer;
}

const Eventqueue = namespace("eventqueue");
const MapLayers = namespace("mapLayers");
const ManualRaiseStore = namespace("manualRaise");

@Component({
	components: {
		multiselect: Multiselect
	}
})
export default class EnhancedMapAlarms extends Vue {
	$refs: any = {
		mappingContainer: HTMLElement
	};

	// Flag to specify if toggle map feature flag enabled, is it enabled for this instance of the re-usable component.
	@Prop({ default: () => false })
	private showMapToggle: boolean;

	@Getter("getMapKey") private mapKey: string;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getCustomMapViewTypes") public customMapViewTypes;

	public enhancedMapping: IEnhancedMapping | null = null;
	public map: IMap | null = null;
	public filterBounds: ILatLngBounds | null = null;
	public markers: EventRecordPositionMap = new EventRecordPositionMap();
	public layersAdded = new LayerMap();

	public eventIcons: EventIconsMap = new EventIconsMap();

	@MapLayers.Action loadMapLayers: (request: MapRequest) => Promise<AxiosResponse<MapLayer>>;
	@MapLayers.Action loadAreaMapLayersBounds: any;
	@MapLayers.Mutation setManualRaiseMarkerPosition: any;
	@MapLayers.Mutation setManualRaiseLayers: any;
	@MapLayers.Mutation setSelectedManualRaiseLayer: any;
	@MapLayers.Mutation setSelectedMapElevation: any;
	@MapLayers.Getter getManualRaiseSelectedLayer: any;

	@Eventqueue.Mutation setQueueMapBounds: any;
	@Eventqueue.Getter("getActiveEvents") events!: any[];
	@Eventqueue.Getter("getActiveFilterID") currentFilterID!: AlarmQueueFilterID;
	@Eventqueue.Getter("getLastUpdatedEvents") eventsLastUpdated!: number;
	@Eventqueue.Getter("getHoverEvent") hoverEvent: any;
	@Eventqueue.Getter DisplayedEvent: FilteredEvent;
	@Eventqueue.Getter getAlarmQueueFilterIDs: AlarmQueueFilterID[];

	// Getter and action for boolean flag which determines whether the map is shown.
	@Eventqueue.Action setHideMapFlag: any;
	@Eventqueue.Getter("getHideMapFlag") hideMapFlag: boolean;

	@ManualRaiseStore.State manualRaiseShown: boolean;
	@ManualRaiseStore.State("areaMapBounds") manualRaiseAreaMapBounds: any;
	@ManualRaiseStore.State("areaLatLngString") manualRaiseAreaLatLngString: string;
	@ManualRaiseStore.Getter("getSelectedRegionId") manualRaiseRegionId: number;
	@ManualRaiseStore.Mutation setManualRaiseShown: (visible: boolean) => void;

	@Getter getHideLabels: any;
	@Getter getMapType: any;
	@Action setMapType: any;

	public mapType: string = "";

	public displayElevation: string = "";
	public elevation: number = 1;
	public elevationList: any[] = [];
	public canGoDown: boolean = false;
	public canGoUp: boolean = false;

	public alarmMarker: IMarker | null = null;

	public layersVisible: boolean = true;

	public searchExpanded: boolean = true;
	public searchType: string = "area";

	public groups: any[] = [];
	public groupSearchLoading = false;
	public selectedGroup: any = {};

	public createdAutocomplete: boolean = false;
	public selectedLocation: any[] = [];
	public locations: any[] = [];
	public locationSearchLoading: boolean = false;
	public autocomplete: google.maps.places.Autocomplete | null = null;

	public filterBoundsChanged: boolean = false;

	public preManualRaiseBounds: ILatLngBounds | null = null;

	public created() {
		this.setManualRaiseShown(false);
	}

	public get addAlarm() {
		return this.manualRaiseShown;
	}

	@Watch("addAlarm")
	public async onAddAlarmChanged(value: boolean) {
		if (this.map != null) {
			if (value) {
				this.map!.setCursor("crosshair");
				if (this.map!.currentViewState == "narrow") {
					await this.loadInBounds(this.map!.mapBounds, true);
				}

				for (let eventRecordID in this.markers) {
					this.markers[eventRecordID].state = "muted" + this.markers[eventRecordID].state;
				}
			} else {
				this.map!.setCursor("grab");
				if (this.alarmMarker != null) {
					this.alarmMarker.remove();
					this.alarmMarker = null;
				}
				this.selectedLocation = [];
				this.selectedGroup = null;

				this.clearMap();
				this.setManualRaiseLayers([]);
				this.setManualRaiseMarkerPosition("");

				for (let eventRecordID in this.markers) {
					this.markers[eventRecordID].state = this.markers[eventRecordID].state.replace("muted", "");
				}
			}
		}
	}

	@Watch("manualRaiseAreaMapBounds")
	public onManualRaiseAreaMapBoundsChanged(value: any, oldValue: any) {
		if (this.map != null) {
			if (value) {
				this.map!.mapBounds = value as ILatLngBounds;
			} else if (oldValue) {
				this.setFilterBounds(this.currentFilterID.filterId, this.currentFilterID.regionId);
			}
		}
	}

	@Watch("manualRaiseAreaLatLngString")
	public onManualRaiseAreaLatLngStringChanged(value: string) {
		if (value && this.map) {
			try {
				const defaultZoomLevel: number = 20;
				this.goToLocation(defaultZoomLevel, convertStringToLatLng(value));
			} catch (e) {
				console.log(`Unable to go to location ${value}`);
			}
		}
	}

	@Watch("getManualRaiseSelectedLayer")
	public onManualRaiseSelectedLayerChanged(value: any, oldValue: any) {
		var newMinElevation = value?.minElevation;
		var oldMinElevation = oldValue?.minElevation;

		if (newMinElevation !== null && newMinElevation !== oldMinElevation) {
			this.gotoElevation({ elevationValue: newMinElevation });
		}
	}

	@Watch("searchType")
	public onSearchTypeChanged(value: string, oldValue: string) {
		if (value == "location") {
			if (!this.createdAutocomplete) {
				this.createdAutocomplete = true;
				this.setAutocomplete();
			}
		}

		this.selectedGroup = null;
	}

	private get MapPosition() {
		return { lat: 0, lng: 0, zoom: 2 };
	}

	@Watch("currentFilterID")
	public onCurrentFilterChange(filter: any, oldFilter: any) {
		if (this.map != null) {
			this.setFilterBounds(filter.filterId, filter.regionId);
		}
	}

	@Watch("eventsLastUpdated")
	public onEventsChanged(lastChanged: any, oldLastChanged: any) {
		this.updateEventMarkers();
	}

	@Watch("DisplayedEvent")
	public onDisplayedEventChanged(event) {
		if (event && event.locations && event.locations.length) {
			const latestLocation = event.locations[event.locations.length - 1];

			const [lat, long] =
				latestLocation.location.length > 0
					? latestLocation.location.split(",")[0].split(" ")
					: latestLocation.latLong.split(",")[0].split(" ");
			this.map!.center = { lat: parseFloat(lat), lng: parseFloat(long) };
		}
	}

	@Watch("hoverEvent")
	public onhoverEventChanged(event: any, oldEvent: any) {
		if (oldEvent != null && this.eventIcons[oldEvent.eventID] != null) {
			this.eventIcons[oldEvent.eventID].forEach(marker => {
				marker.state = marker.state.replace("highlight", "");
			});
		}

		if (event != null && this.eventIcons[event.eventID] != null) {
			this.eventIcons[event.eventID].forEach(marker => {
				marker.revertState = marker.state;
				if (marker.state == "active") {
					marker.state = "highlightactive";
				} else if (marker.state == "processing") {
					marker.state = "highlightprocessing";
				}
			});
		}
	}

	private setBounds(bounds: ILatLngBounds) {
		bounds.north = bounds.north || 400;
		bounds.east = bounds.east || 400;
		bounds.west = bounds.west || 400;
		bounds.south = bounds.south || 400;

		this.setQueueMapBounds(bounds);
		this.map!.mapBounds = bounds;
	}

	private updateEventMarkers() {
		if (this.manualRaiseShown) return;

		if (this.map != null) {
			let events = this.events;

			if (events == null) return;

			var bounds = {} as ILatLngBounds;
			var boundsSet = false;

			let eventIDs = new Set<number>();

			let hasNewMarker = false;
			events.forEach((event: any) => {
				if (event.locations !== null && !hasNewMarker) {
					event.locations.forEach((location: any) => {
						if (this.markers[location.eventRecordID] == null) hasNewMarker = true;
					});
				}
			});

			events.forEach((event: any) => {
				eventIDs.add(event.eventID);

				if (event.locations !== null) {
					event.locations.forEach((location: any) => {
						if (this.markers[location.eventRecordID] == null) {
							let latLong = location.latLong;

							if (location.location != "") latLong = location.location;

							let position = convertStringToLatLng(latLong);
							let newMarker = this.enhancedMapping.createMarker(
								this.map!,
								location.mapLayerItemID,
								location.eventRecordID,
								4,
								event.eventID,
								location.details,
								position,
								null,
								null,
								null,
								true
							);
							newMarker.isClickable = false;

							bounds = extend(bounds, position);
							boundsSet = true;

							this.markers[location.eventRecordID] = newMarker;

							if (this.eventIcons[event.eventID] == null)
								this.eventIcons[event.eventID] = new Array<IMarker>();

							this.eventIcons[event.eventID].push(newMarker);
						} else if (this.filterBoundsChanged || hasNewMarker) {
							let latLong = location.latLong;

							if (location.location != "") latLong = location.location;

							let position = convertStringToLatLng(latLong);

							bounds = extend(bounds, position);
							boundsSet = true;
						}

						if (this.addAlarm) {
							this.markers[location.eventRecordID].state =
								event.inProcessing == 1 || event.unParkAt != null ? "mutedprocessing" : "mutedactive";
						} else {
							//highlight

							let marker = this.markers[location.eventRecordID];

							if (marker.state != null && marker.state.indexOf("highlight") > -1) {
								marker.state =
									event.inProcessing == 1 || event.unParkAt != null
										? "highlightprocessing"
										: "highlightactive";
							} else {
								marker.state =
									event.inProcessing == 1 || event.unParkAt != null ? "processing" : "active";
							}
						}
					});
				}
			});

			for (var eventID in this.eventIcons) {
				if (!eventIDs.has(parseInt(eventID))) {
					this.eventIcons[eventID].forEach(eventMarker => {
						if (eventMarker.eventRecordID != null) delete this.markers[eventMarker.eventRecordID!];

						eventMarker.remove();
					});

					delete this.eventIcons[eventID];
				}
			}

			if (boundsSet) {
				if (this.filterBounds != null) {
					bounds = union(bounds, this.filterBounds!);
				}

				if (this.manualRaiseAreaMapBounds == null) this.setBounds(bounds);
				else this.preManualRaiseBounds = bounds;
			}

			this.filterBoundsChanged = false;
		} else {
			console.log("Map is null");
		}
	}

	private setFilterBounds(filterID: number, regionID: number) {
		if (filterID == -1) {
			this.filterBounds = null;
			this.filterBoundsChanged = true;
		} else if (filterID == 0) {
			let openFilters = this.getAlarmQueueFilterIDs;
			if (openFilters.length > 0) {
				let requestPromises = [];

				openFilters.forEach(filter => {
					requestPromises.push(
						axiosInstance.get(
							"/AlarmQueueFilters/Bounds/" +
								filter.filterId +
								(filter.regionId ? "?regionId=" + filter.regionId : "")
						)
					);
				});

				Promise.all(requestPromises).then(values => {
					let allBounds: ILatLngBounds = null;

					values.forEach(response => {
						if (response.data && response.data.anyBounds) {
							let filterBounds = response.data as ILatLngBounds;

							if (allBounds == null) {
								allBounds = {
									north: filterBounds.north,
									east: filterBounds.east,
									south: filterBounds.south,
									west: filterBounds.west
								};
							} else {
								allBounds = union(allBounds, filterBounds);
							}
						}
					});

					this.filterBounds = allBounds;
					if (allBounds != null) {
						this.setBounds(this.filterBounds);
					}
					this.filterBoundsChanged = true;
				});
			}
		} else {
			axiosInstance
				.get("/AlarmQueueFilters/Bounds/" + filterID + (regionID ? "?regionId=" + regionID : ""))
				.then(response => {
					let filterBounds = response.data;

					if (filterBounds != null && filterBounds.anyBounds) {
						this.filterBounds = {
							north: filterBounds.north,
							east: filterBounds.east,
							south: filterBounds.south,
							west: filterBounds.west
						};
						this.setBounds(this.filterBounds);
					} else {
						this.filterBounds = null;
					}

					this.filterBoundsChanged = true;
				});
		}
	}

	public async mounted() {
		this.enhancedMapping = await getEnhancedMapping("google", this.mapKey);

		let pos = this.MapPosition;
		this.map = this.enhancedMapping.createMap(
			this.$refs.mappingContainer,
			pos,
			pos.zoom,
			false,
			this.getMapType,
			getMapStyle(this.getMapType, this.getHideLabels),
			this.customMapViewTypes
		);

		this.mapType = this.getMapType;

		if (!this.createdAutocomplete) {
			this.createdAutocomplete = true;
			this.setAutocomplete();
		}

		this.setFilterBounds(this.currentFilterID.filterId, this.currentFilterID.regionId);
		this.updateEventMarkers();

		this.map!.registerEvent("onMapTypeChanged", (mapType: string) => {
			this.mapType = mapType;
			this.setMapType(mapType);

			this.map!.map.setOptions({
				styles: getMapStyle(this.getMapType, this.getHideLabels)
			});
		});

		this.map!.registerEvent("onClick", async (latLong: any) => {
			if (this.addAlarm) {
				await this.setMarkerPosition(latLong);
			}
		});

		this.map!.registerEvent("onViewportChangedDelayed", async () => {
			if (this.addAlarm) {
				if (this.map!.currentViewState == "narrow") {
					await this.loadInBounds(this.map!.mapBounds, true);
				} else {
					this.clearMap();
				}
			}
		});
	}

	beforeDestroy() {
		this.map.reset();

		if (this.alarmMarker != null) {
			this.alarmMarker.remove();
			this.alarmMarker = null;
		}

		this.clearMap();
	}

	private async setMarkerPosition(latLng: any) {
		if (this.alarmMarker != null) {
			this.alarmMarker.remove();
			this.alarmMarker = null;
		}

		this.alarmMarker = this.enhancedMapping.createMarker(
			this.map!,
			null,
			null,
			4,
			null,
			"Alarm",
			latLng,
			null,
			null,
			null,
			true
		);
		this.alarmMarker.state = "active";
		this.alarmMarker.isDraggable = true;
		this.alarmMarker.isClickable = false;

		this.alarmMarker.dragend = async (latLong: ILatLng) => {
			await this.loadLayersNearLocation(latLong);
			this.setManualRaiseMarkerPosition(latLong.lat + " " + latLong.lng);
		};

		if (latLng instanceof google.maps.LatLng) {
			this.setManualRaiseMarkerPosition(latLng.lat() + " " + latLng.lng());
		} else {
			this.setManualRaiseMarkerPosition(latLng.lat + " " + latLng.lng);
		}

		this.map!.center = latLng;
		await this.loadLayersNearLocation(latLng);
	}

	public async loadLayersNearLocation(latLong: ILatLng) {
		var NE: { lat: number; lng: number } = addMeterToCoord(10, 10, latLong);
		var SW: { lat: number; lng: number } = addMeterToCoord(-10, -10, latLong);

		var bounds = {
			north: NE.lat,
			south: SW.lat,
			east: NE.lng,
			west: SW.lng,
			elevation: this.elevation,
			layersOnly: true,
			regionId: this.manualRaiseRegionId
		} as MapRequest;

		if (this.map!.currentViewState !== "narrow") delete bounds.elevation;

		const {
			data: { layers }
		} = await this.loadMapLayers(bounds);

		this.setManualRaiseLayers(layers);

		if (layers.length == 1) {
			// This setter is not auto updating the dropdown box
			this.setSelectedManualRaiseLayer(layers[0]);
		} else {
			if (this.selectedGroup != null) {
				let selectedGroupLayers = layers.filter((layer: any) => layer.groupID == this.selectedGroup.groupID);
				if (selectedGroupLayers.length == 1) {
					this.setSelectedManualRaiseLayer(selectedGroupLayers[0]);
				}
			}
		}
	}

	private async loadInBounds(mapBounds: ILatLngBounds, layersVisible: boolean) {
		this.layersVisible = layersVisible;

		let bounds = {
			north: mapBounds.north,
			south: mapBounds.south,
			east: mapBounds.east,
			west: mapBounds.west,
			elevation: this.elevation,
			regionId: this.manualRaiseRegionId
		} as MapRequest;

		const {
			data: { layers, elevations }
		} = await this.loadMapLayers(bounds);

		this.displayLayers(layers);
		this.elevationList = elevations;

		const currentElevation = elevations.find(elevationFloor => elevationFloor.elevationValue == this.elevation);
		if (currentElevation != null) {
			this.displayElevation = currentElevation.name;
		}

		this.canGoDown = this.elevationList.some(elevation => elevation.elevationValue < this.elevation);
		this.canGoUp = this.elevationList.some(elevation => elevation.elevationValue > this.elevation);
	}

	private displayLayers(layers: MapLayerData[]) {
		const itemLayerIDs: string[] = [];

		layers.forEach(layer => {
			const elevation = this.elevation;

			let isElevated =
				(layer.minElevation == null || layer.minElevation! <= elevation) &&
				(layer.maxElevation == null || layer.maxElevation! >= elevation);

			if (isElevated) {
				itemLayerIDs.push(layer.mapLayerID.toString());

				const mapLayerImageUrl = this.buildMapLayerImageUrl(layer.mapLayerID, this.manualRaiseRegionId);

				if (this.layersAdded[layer.mapLayerID] == undefined) {
					this.layersAdded[layer.mapLayerID] = this.enhancedMapping.createOverlay(
						this.map!,
						layer.mapLayerID,
						layer.title,
						stringToBounds(layer.ne, layer.sw), // boundaries
						layer.rotation, // rotation
						mapLayerImageUrl, //imageUrl
						layer.minElevation, // minElevation,
						layer.maxElevation, // maxElevation
						this.layersVisible // , visible
					);
				}
			}
		});

		for (var layerId in this.layersAdded) {
			if (itemLayerIDs.indexOf(layerId) == -1) {
				var layer = this.layersAdded[layerId];

				layer.remove();
				delete this.layersAdded[layerId];
			}
		}
	}

	/**
	 * Builds the map layer image URL for a given map layer and region id.
	 *
	 * @remarks Preferably we'd use the URL class for this, but our default production baseURL is simply '/api',
	 * and the URL class doesn't support using relative base paths.
	 */
	private buildMapLayerImageUrl(mapLayerId: number, regionId: number = null) {
		// Get the base URL and ensure it ends with a '/'.
		let baseUrl = axiosInstance.defaults.baseURL.trim();
		if (!baseUrl.endsWith("/")) {
			baseUrl += "/";
		}

		// Build the URL to use to load the map layer
		let mapLayerUrl = `MapLayers/Image/${mapLayerId}`;
		if (regionId) {
			mapLayerUrl += `?regionId=${regionId}`;
		}

		return `${baseUrl}${mapLayerUrl}`;
	}

	private clearMap() {
		for (var layerId in this.layersAdded) {
			let layer = this.layersAdded[layerId];

			layer.remove();
			delete this.layersAdded[layerId];
		}

		for (var eventID in this.eventIcons) {
			this.eventIcons[eventID].forEach(eventMarker => {
				delete this.markers[eventMarker.eventRecordID!];
				eventMarker.remove();
			});

			delete this.eventIcons[eventID];
		}
	}

	public async asyncFind(query: string) {
		try {
			this.groupSearchLoading = true;
			this.groups = await api.searchGroup(query);
		} finally {
			this.groupSearchLoading = false;
		}
	}

	public async areaSelected(area: UserGroup) {
		const bounds = await this.loadAreaMapLayersBounds(area.groupID);

		if (bounds && bounds !== "") {
			this.map!.mapBounds = bounds;
		} else {
			if (area.address) {
				const geoCoder = new GPSService();

				try {
					const geoCodeResponse = await geoCoder.GeoCodeLookup(area.address, this.mapKey);
					if (geoCodeResponse.results && geoCodeResponse.results.length > 0) {
						const addressGeometry = geoCodeResponse.results[0].geometry;

						if (addressGeometry.viewport) {
							this.map!.mapBounds = {
								north: addressGeometry.viewport.northeast.lat,
								east: addressGeometry.viewport.northeast.lng,
								south: addressGeometry.viewport.southwest.lat,
								west: addressGeometry.viewport.southwest.lng
							};
						} else if (addressGeometry.location) {
							this.map!.mapBounds = {
								north: addressGeometry.location.lat,
								east: addressGeometry.location.lng,
								south: addressGeometry.location.lat,
								west: addressGeometry.location.lng
							};
						}
					}
				} catch (err) {
					// we dont need to action anything here.
				}
			}
		}
	}

	public setAutocomplete() {
		setTimeout(() => {
			this.autocomplete = new google.maps.places.Autocomplete(
				document.getElementById("tbLocation") as HTMLInputElement
			);

			this.autocomplete!.bindTo("bounds", this.map!.map);
			(this.autocomplete! as any).setFields(["address_components", "geometry", "name"]);

			this.autocomplete!.addListener("place_changed", async () => {
				let place = this.autocomplete!.getPlace();

				if (place.geometry) {
					if (place.geometry.viewport) {
						this.map!.map.fitBounds(place.geometry.viewport);
					} else if (place.geometry.location) {
						this.map!.map.setCenter(place.geometry.location);
						this.map!.map.setZoom(17);
					}

					if (place.geometry.location) {
						await this.setMarkerPosition(place.geometry.location);
					}
				}
			});
		}, 0.5 * this.$config.ANIMATION_DURATION);
	}

	public async upLevel() {
		let higherFloors = this.elevationList.filter(elevation => elevation.elevationValue > this.elevation);
		if (higherFloors.length > 0) {
			this.elevation = higherFloors[0].elevationValue;
			await this.updateElevation();
		}
	}

	public async downLevel() {
		let lowerFloors = this.elevationList.filter(elevation => elevation.elevationValue < this.elevation);
		if (lowerFloors.length > 0) {
			this.elevation = lowerFloors[lowerFloors.length - 1].elevationValue;
			await this.updateElevation();
		}
	}

	public async gotoElevation(elevation: any) {
		this.elevation = elevation.elevationValue;
		this.displayElevation = elevation.name;
		await this.updateElevation();
	}

	public async updateElevation() {
		// Stores selected elevation from the map controls into the store so that other components may access
		this.setSelectedMapElevation(this.elevation);
		this.clearMap();
		await this.loadInBounds(this.map!.mapBounds, this.map!.currentViewState == "narrow");

		if (this.alarmMarker != null) {
			await this.loadLayersNearLocation(this.alarmMarker.position);
		}
	}

	// Used for the toggle maps feature flag.
	private get isToggleMapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "Maps", "ToggleEventQueue"]) && this.showMapToggle;
	}

	/**
	 * move to the map to the givem position and zoom level
	 *
	 * @param {number} zoom - zoom level to set the map to
	 * @param {GeoCodeLocation} location - LatLong coordinates to center the map on
	 */
	private goToLocation(zoom: number = 10, location: GeoCodeLocation): void {
		if (this.map && location && location.lat && location.lng) {
			this.map.map.zoom = zoom;
			this.map.map.setCenter({ lat: location.lat, lng: location.lng });
		}
	}
}
